import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');

export default function SupplierQuotations({ purchaseRequestId, items: prItems }) {
  const { user } = useAuth();
  const canManage = ['super_admin','procurement_logistics_officer'].includes(user?.role);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [threshold, setThreshold] = useState(10);
  const [docFile, setDocFile] = useState(null);
  const [form, setForm] = useState({
    supplier_id:'', supplier_name:'', quote_date:'',
    valid_until:'', delivery_days:'', delivery_cost:'', notes:''
  });
  const [quoteItems, setQuoteItems] = useState([{
    description:'', quantity:1, unit:'', unit_price:'', alert_explanation:''
  }]);
  const [suppliers, setSuppliers] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/supplier-quotations/purchase-request/'+purchaseRequestId),
      get('/supplier-quotations/threshold'),
      get('/suppliers')
    ]).then(([q, t, s]) => {
      setQuotes(q.data||[]);
      setThreshold(t.threshold||10);
      setSuppliers(s.data||[]);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [purchaseRequestId]);

  // Pre-fill items from purchase request
  useEffect(() => {
    if (prItems && prItems.length > 0) {
      setQuoteItems(prItems.map(item => ({
        description: item.description||'',
        quantity: item.quantity||1,
        unit: item.unit||'',
        unit_price: '',
        alert_explanation: ''
      })));
    }
  }, [prItems]);

  const addItem = () => setQuoteItems([...quoteItems,
    {description:'',quantity:1,unit:'',unit_price:'',alert_explanation:''}]);
  const removeItem = i => setQuoteItems(quoteItems.filter((_,idx)=>idx!==i));
  const updateItem = (i, field, val) => {
    const updated = [...quoteItems];
    updated[i] = {...updated[i],[field]:val};
    setQuoteItems(updated);
  };

  const totalEstimated = quoteItems.reduce((sum,item) =>
    sum+(parseFloat(item.unit_price||0)*parseFloat(item.quantity||1)), 0)
    + parseFloat(form.delivery_cost||0);

  const submitQuote = async () => {
    if (!form.supplier_name && !form.supplier_id) {
      alert('Supplier name or selection required'); return;
    }
    // Check if any alerts need explanation
    const alertItems = quoteItems.filter(item => {
      return item._has_alert && !item.alert_explanation;
    });
    if (alertItems.length > 0) {
      alert('Please provide an explanation for price alerts before saving'); return;
    }
    setSaving(true);
    try {
      const result = await post(
        '/supplier-quotations/purchase-request/'+purchaseRequestId,
        { ...form, items: quoteItems }
      );
      setPriceAlerts(result.price_alerts||[]);

      // Upload document if provided
      if (docFile && result.data?.id) {
        const fd = new FormData();
        fd.append('document', docFile);
        const token = localStorage.getItem('bb_token');
        await fetch('/api/supplier-quotations/'+result.data.id+'/document', {
          method:'POST',
          headers:{ Authorization:'Bearer '+token },
          body: fd
        });
      }

      setShowForm(false);
      setDocFile(null);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const recommend = async (id) => {
    if (!window.confirm('Mark this as the recommended supplier? Doris will be notified to approve.')) return;
    try {
      await put('/supplier-quotations/'+id+'/recommend', {});
      load();
    } catch(err) { alert(err.message); }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm('Delete this quotation?')) return;
    try {
      await del('/supplier-quotations/'+id);
      load();
    } catch(err) { alert(err.message); }
  };

  const lowestQuote = quotes.length > 0
    ? quotes.reduce((min, q) => q.total_amount < min.total_amount ? q : min, quotes[0])
    : null;

  if (loading) return <div className="spinner"></div>;

  return (
    <div style={{marginTop:20}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:600,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em'}}>
          Supplier Quotations ({quotes.length})
        </div>
        {canManage && (
          <button className="btn btn-outline btn-sm" onClick={()=>setShowForm(true)}>
            + Add Quotation
          </button>
        )}
      </div>

      {/* Price alert notifications */}
      {priceAlerts.length > 0 && (
        <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,
          padding:12,marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:600,color:'#8F2E24',marginBottom:8}}>
            ⚠ Price Alert — {priceAlerts.length} item{priceAlerts.length>1?'s':''} exceed {threshold}% threshold
          </div>
          {priceAlerts.map((alert,i) => (
            <div key={i} style={{fontSize:11,color:'#555',marginBottom:4}}>
              <strong>{alert.item}</strong>: Last price {fmt(alert.last_price)} →
              New price {fmt(alert.new_price)}
              <span style={{color:'#8F2E24',fontWeight:600}}> (+{alert.variance_pct}%)</span>
            </div>
          ))}
        </div>
      )}

      {quotes.length === 0 ? (
        <div style={{background:'#F9F9F9',borderRadius:8,padding:20,textAlign:'center',
          fontSize:13,color:'#8A8A8A'}}>
          No quotations yet. {canManage && 'Add quotations to compare suppliers.'}
        </div>
      ) : (
        <>
          {/* Comparison table */}
          {quotes.length > 1 && (
            <div style={{overflowX:'auto',marginBottom:16}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'#3B2A21'}}>
                    <th style={{padding:'8px 12px',textAlign:'left',color:'#A8893D',fontWeight:600}}>Item</th>
                    {quotes.map(q=>(
                      <th key={q.id} style={{padding:'8px 12px',textAlign:'right',color:'#A8893D',fontWeight:600}}>
                        {q.supplier_name||q.supplier_name_ref}
                        {q.is_recommended && <span style={{marginLeft:4,fontSize:9,
                          background:'#556B2F',color:'#fff',padding:'1px 6px',borderRadius:10}}>★ Recommended</span>}
                        {lowestQuote?.id === q.id && (
                          <span style={{marginLeft:4,fontSize:9,
                            background:'#2E6B3A',color:'#fff',padding:'1px 6px',borderRadius:10}}>Lowest</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Items */}
                  {quotes[0]?.items?.map((item,i) => (
                    <tr key={i} style={{borderBottom:'1px solid #F2EFE7',
                      background:i%2===0?'#fff':'#FAFAFA'}}>
                      <td style={{padding:'8px 12px',color:'#3B2A21'}}>{item.item_description}</td>
                      {quotes.map(q => {
                        const qItem = q.items?.[i];
                        const hasAlert = qItem?.price_alert;
                        return (
                          <td key={q.id} style={{padding:'8px 12px',textAlign:'right',
                            color:hasAlert?'#8F2E24':'#3B2A21',fontWeight:hasAlert?600:400}}>
                            {qItem ? fmt(qItem.unit_price) : '—'}
                            {hasAlert && <span style={{marginLeft:4,fontSize:9}}>⚠</span>}
                            {qItem?.price_variance_pct && (
                              <div style={{fontSize:9,color:parseFloat(qItem.price_variance_pct)>0?'#8F2E24':'#2E6B3A'}}>
                                {parseFloat(qItem.price_variance_pct)>0?'+':''}{parseFloat(qItem.price_variance_pct).toFixed(1)}%
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Delivery */}
                  <tr style={{borderBottom:'1px solid #F2EFE7',background:'#F9F9F9'}}>
                    <td style={{padding:'8px 12px',color:'#8A8A8A',fontStyle:'italic'}}>Delivery Cost</td>
                    {quotes.map(q=>(
                      <td key={q.id} style={{padding:'8px 12px',textAlign:'right',color:'#8A8A8A'}}>
                        {fmt(q.delivery_cost)}
                      </td>
                    ))}
                  </tr>
                  {/* Total */}
                  <tr style={{background:'#F2EFE7',fontWeight:700}}>
                    <td style={{padding:'8px 12px',color:'#3B2A21'}}>Total</td>
                    {quotes.map(q=>(
                      <td key={q.id} style={{padding:'8px 12px',textAlign:'right',
                        color:lowestQuote?.id===q.id?'#2E6B3A':'#3B2A21',fontWeight:700}}>
                        {fmt(q.total_amount)}
                        {q.delivery_days && <div style={{fontSize:10,color:'#8A8A8A',fontWeight:400}}>
                          {q.delivery_days} days delivery
                        </div>}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Individual quote cards */}
          {quotes.map(q => (
            <div key={q.id} style={{background:'#fff',border:'1px solid '+(q.is_recommended?'#556B2F':'var(--border-light)'),
              borderRadius:10,padding:14,marginBottom:8}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                    <span style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>
                      {q.supplier_name||q.supplier_name_ref}
                    </span>
                    {q.is_recommended && (
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                        background:'#E8F5EB',color:'#2E6B3A'}}>★ Recommended</span>
                    )}
                    {lowestQuote?.id === q.id && (
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                        background:'#EDF4FF',color:'#1E4FAD'}}>Lowest Price</span>
                    )}
                  </div>
                  <div style={{fontSize:11,color:'#8A8A8A'}}>
                    Total: <strong style={{color:'#556B2F'}}>{fmt(q.total_amount)}</strong>
                    {q.delivery_days && <span> · {q.delivery_days} days delivery</span>}
                    {q.valid_until && <span> · Valid until: {q.valid_until}</span>}
                  </div>
                  {q.notes && <div style={{fontSize:11,color:'#555',marginTop:4}}>{q.notes}</div>}
                  {q.document_url && (
                    <a href={q.document_url} target="_blank" rel="noreferrer"
                      style={{fontSize:11,color:'#556B2F',marginTop:4,display:'inline-block'}}>
                      📄 View Document
                    </a>
                  )}
                  {/* Price alerts in items */}
                  {q.items?.some(i=>i.price_alert) && (
                    <div style={{fontSize:11,color:'#8F2E24',marginTop:4}}>
                      ⚠ Contains price alerts — review before approving
                    </div>
                  )}
                </div>
                {canManage && (
                  <div style={{display:'flex',gap:6,marginLeft:12}}>
                    {!q.is_recommended && (
                      <button className="btn btn-outline btn-sm"
                        style={{fontSize:11,color:'#556B2F',borderColor:'#556B2F'}}
                        onClick={()=>recommend(q.id)}>
                        ★ Recommend
                      </button>
                    )}
                    <button className="btn btn-ghost btn-sm"
                      style={{fontSize:11,color:'#8F2E24'}}
                      onClick={()=>deleteQuote(q.id)}>✕</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Add Quotation Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:600}}>
            <div className="modal-hdr">
              <span className="modal-title">Add Supplier Quotation</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'70vh',overflowY:'auto'}}>
              <div style={{background:'#FFF3DC',borderRadius:8,padding:'8px 12px',
                marginBottom:16,fontSize:12,color:'#8B5E0A'}}>
                ⚠ Price Alert Threshold: <strong>{threshold}%</strong> — Items exceeding this increase will be flagged
              </div>

              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Supplier *</label>
                  <select className="form-control" value={form.supplier_id}
                    onChange={e=>{
                      const sup = suppliers.find(s=>s.id===e.target.value);
                      setForm({...form,supplier_id:e.target.value,
                        supplier_name:sup?.name||form.supplier_name});
                    }}>
                    <option value="">Select supplier...</option>
                    {suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Or Enter Name</label>
                  <input className="form-control" value={form.supplier_name}
                    onChange={e=>setForm({...form,supplier_name:e.target.value})}
                    placeholder="Supplier name" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Quote Date</label>
                  <input type="date" className="form-control" value={form.quote_date}
                    onChange={e=>setForm({...form,quote_date:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Valid Until</label>
                  <input type="date" className="form-control" value={form.valid_until}
                    onChange={e=>setForm({...form,valid_until:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Delivery Days</label>
                  <input type="number" className="form-control" value={form.delivery_days}
                    onChange={e=>setForm({...form,delivery_days:e.target.value})}
                    placeholder="e.g. 3" min="0" />
                </div>
              </div>

              {/* Items */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:10,fontWeight:600,color:'#556B2F',
                  textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>
                  Items *
                </div>
                {quoteItems.map((item,i)=>(
                  <div key={i} style={{background:'#F9F9F9',borderRadius:8,padding:12,marginBottom:8,
                    border:item._has_alert?'1px solid #F5B8B8':'1px solid transparent'}}>
                    <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr',gap:8,marginBottom:6}}>
                      <input className="form-control" value={item.description}
                        onChange={e=>updateItem(i,'description',e.target.value)}
                        placeholder="Item description *" style={{fontSize:12}} />
                      <input type="number" className="form-control" value={item.quantity}
                        onChange={e=>updateItem(i,'quantity',e.target.value)}
                        placeholder="Qty" min="1" style={{fontSize:12}} />
                      <input className="form-control" value={item.unit}
                        onChange={e=>updateItem(i,'unit',e.target.value)}
                        placeholder="Unit" style={{fontSize:12}} />
                      <input type="number" className="form-control" value={item.unit_price}
                        onChange={e=>updateItem(i,'unit_price',e.target.value)}
                        placeholder="Unit price" style={{fontSize:12}} />
                    </div>
                    {item._has_alert && (
                      <div style={{marginTop:6}}>
                        <div style={{fontSize:11,color:'#8F2E24',marginBottom:4}}>
                          ⚠ Price alert: explain why this price is higher than {threshold}%
                        </div>
                        <input className="form-control" value={item.alert_explanation}
                          onChange={e=>updateItem(i,'alert_explanation',e.target.value)}
                          placeholder="Reason for price increase..." style={{fontSize:12}} />
                      </div>
                    )}
                    {quoteItems.length > 1 && (
                      <button className="btn btn-ghost btn-sm" style={{color:'#8F2E24',marginTop:4}}
                        onClick={()=>removeItem(i)}>✕ Remove</button>
                    )}
                  </div>
                ))}
                <button className="btn btn-outline btn-sm" onClick={addItem}>+ Add Item</button>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Delivery Cost</label>
                  <input type="number" className="form-control" value={form.delivery_cost}
                    onChange={e=>setForm({...form,delivery_cost:e.target.value})}
                    placeholder="0" min="0" />
                </div>
              </div>

              {totalEstimated > 0 && (
                <div style={{textAlign:'right',fontSize:14,fontWeight:700,
                  color:'#556B2F',marginBottom:12}}>
                  Total: {fmt(totalEstimated)}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes}
                  onChange={e=>setForm({...form,notes:e.target.value})}
                  placeholder="Negotiation notes, terms, conditions..." />
              </div>
              <div className="form-group">
                <label className="form-label">Upload Quotation Document (optional)</label>
                <input type="file" className="form-control"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e=>setDocFile(e.target.files[0])} />
                {docFile && <div style={{fontSize:11,color:'#556B2F',marginTop:4}}>
                  📄 {docFile.name}
                </div>}
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitQuote} disabled={saving}>
                {saving?'Saving...':'Save Quotation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
